import { Info } from 'lucide-react';
import { cn } from '../lib/cn';
import { ROLE_META } from '../lib/roles';
import { useHelp } from '../lib/help';
import type { Role } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// HelperCard — "What lives here" explainer card.
//
// Gated on the global help toggle (HelpFab / useHelp). Hidden by default;
// reveals when help is ON. The per-card dismiss X has been removed — the
// global toggle replaces it. id + dismissible are accepted for call-site
// backward compat but are not used.
// ─────────────────────────────────────────────────────────────────────────────

type Point = { role?: Role; text: string };

export function HelperCard({
  title,
  intro,
  points,
  className,
  // Kept optional for backward compat — not used (global toggle replaces dismiss)
  id: _id,
  dismissible: _dismissible,
}: {
  id?: string;
  title: string;
  intro?: string;
  points?: Point[];
  className?: string;
  dismissible?: boolean;
}) {
  const { helpOpen } = useHelp();
  if (!helpOpen) return null;

  // House language: borderless raised fill, no forge gradient, no glow line.
  return (
    <div className={cn('relative rounded-lg bg-white/[0.04] p-4', className)}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/[0.06]">
          <Info className="h-3 w-3 text-ink-secondary" strokeWidth={2.5} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-ink-secondary">
            {title}
          </p>
          {intro && (
            <p className="mt-1.5 text-sm lg:text-base leading-relaxed text-ink-body">{intro}</p>
          )}

          {points && points.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {points.map((p, i) => {
                const meta = p.role ? ROLE_META[p.role] : null;
                return (
                  <li key={i} className="flex items-start gap-2 text-sm lg:text-base text-ink-body">
                    {meta ? (
                      <span
                        className={cn(
                          'mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                          meta.dot
                        )}
                        title={meta.label}
                      />
                    ) : (
                      <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ink-meta" />
                    )}
                    <span className="flex-1">
                      {p.role && (
                        <span className={cn('mr-1.5 font-display text-[10px] font-black uppercase tracking-wider', meta?.text)}>
                          {meta?.label}:
                        </span>
                      )}
                      {p.text}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
